import React, { useState, useEffect, useCallback } from 'react';
import InlineTableSkeleton from "../../components/layout/InlineTableSkeleton";
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import {
    CircleQuestionMark, Plus, Save, X, CheckCircle, XCircle, Search,
    Eye, Loader2, ChevronLeft, ChevronRight, RotateCcw, Download, Upload, FileSpreadsheet,
    AlertTriangle, Music, Scale, FileText,
} from 'lucide-react';
import { ScoringTab, PromptTab } from './aiFeatureTabs';

const CATEGORIES = ['behavioral', 'role-specific', 'leadership'];
const DIFFICULTIES = ['junior', 'mid', 'senior'];
const JOB_TYPES = ['technical', 'non-technical', 'business'];

const emptyForm = { questionText: '', category: '', industryId: '', difficultyLevel: '', jobType: '' };

const blobFileName = (blobPath) => blobPath?.split('/').pop() || '';

const audioCoverage = (q) => {
    const count = (q.BlobPath ? 1 : 0) + (q.BlobPathFemale ? 1 : 0);
    if (count === 2) return 'complete';
    if (count === 1) return 'partial';
    return 'none';
};

function AudioPickerField({ label, file, setFile, existingBlobPath, isDarkMode }) {
    const filled = Boolean(file || existingBlobPath);
    return (
        <div>
            <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {label}
            </label>
            <div className="flex items-center gap-2">
                <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                    filled
                        ? (isDarkMode ? 'border border-slate-600 text-slate-300 hover:bg-slate-700' : 'border border-slate-300 text-slate-700 hover:bg-slate-50')
                        : (isDarkMode ? 'border border-dashed border-slate-600 text-slate-400 hover:bg-slate-700' : 'border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50')
                }`}>
                    <Music size={16} className="flex-shrink-0" />
                    <span className="truncate">
                        {file
                            ? file.name
                            : existingBlobPath
                                ? blobFileName(existingBlobPath)
                                : 'Choose audio file...'}
                    </span>
                    <input
                        type="file"
                        accept="audio/*"
                        className="hidden"
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) setFile(f);
                            e.target.value = '';
                        }}
                    />
                </label>
                {file && (
                    <button
                        type="button"
                        onClick={() => setFile(null)}
                        title="Remove selected file"
                        className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                        <X size={16} />
                    </button>
                )}
            </div>
            {!file && existingBlobPath && (
                <p className={`mt-1.5 text-xs flex items-center gap-1 ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                    <CheckCircle size={12} /> Choosing a new file will replace the one on record.
                </p>
            )}
        </div>
    );
}

export default function InterviewManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('questions');
    // Separate General/STAR toggles for the Scoring and Prompt tabs -- InterviewScoringCategories
    // and AIPromptConfig each hold both rubrics, split by ScoringType / FeatureName, so switching
    // one tab's toggle doesn't affect the other.
    const [scoringMode, setScoringMode] = useState('general'); // 'general' | 'star'
    const [promptMode, setPromptMode] = useState('general'); // 'general' | 'star'

    const [questions, setQuestions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 });
    const [industries, setIndustries] = useState([]);

    const [categoryFilter, setCategoryFilter] = useState('');
    const [industryFilter, setIndustryFilter] = useState('');
    const [jobTypeFilter, setJobTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null); // null = creating
    const [form, setForm] = useState(emptyForm);
    const [audioFile, setAudioFile] = useState(null); // newly-selected File for the question's male-voice audio, if any
    const [audioFemaleFile, setAudioFemaleFile] = useState(null); // newly-selected File for the question's female-voice audio, if any
    const [saving, setSaving] = useState(false);
    const [savingId, setSavingId] = useState(null); // question currently being activated/deactivated

    const [batchFile, setBatchFile] = useState(null);
    const [batchUploading, setBatchUploading] = useState(false);
    const [batchDownloading, setBatchDownloading] = useState(false);
    const [batchResult, setBatchResult] = useState(null); // { saved, warnings, errors, total_rows, row_results }
    const [batchError, setBatchError] = useState('');

    // CATEGORIES text colors
    const CAT_COLORS = {
        'behavioral': `${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`,
        'role-specific': `${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`,
        'leadership': `${isDarkMode ? 'text-red-400' : 'text-red-600'}`,
    }

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    // debounce search input, same 500ms pattern used elsewhere in this app (MockInterview.js)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('/interview/questions', {
                params: {
                    page: pagination.page,
                    per_page: pagination.per_page,
                    category: categoryFilter || undefined,
                    industry_id: industryFilter || undefined,
                    job_type: jobTypeFilter || undefined,
                    search: debouncedSearchTerm || undefined,
                    include_inactive: statusFilter !== 'active' ? 'true' : undefined,
                },
            });
            let rows = response.data?.questions || [];
            if (statusFilter === 'inactive') {
                rows = rows.filter((q) => !q.isActive);
            }
            setQuestions(rows);
            setPagination((p) => ({ ...p, total: response.data?.total || 0 }));
        } catch (err) {
            console.error('Error fetching questions:', err);
            setError(err.response?.data?.error || 'Failed to load interview questions');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.per_page, categoryFilter, industryFilter, jobTypeFilter, debouncedSearchTerm, statusFilter]);

    useEffect(() => {
        if (activeTab === 'questions') fetchQuestions();
    }, [activeTab, fetchQuestions]);

    useEffect(() => {
        api.get('/jobs/industries')
            .then((res) => setIndustries(res.data?.industries || []))
            .catch((err) => console.error('Error fetching industries:', err));
    }, []);

    const openAddModal = () => {
        setEditingQuestion(null);
        setForm(emptyForm);
        setAudioFile(null);
        setAudioFemaleFile(null);
        setShowModal(true);
    };

    const openEditModal = (question) => {
        setEditingQuestion(question);
        setForm({
            questionText: question.QuestionText,
            category: question.Category,
            industryId: question.IndustryID || '',
            difficultyLevel: question.DifficultyLevel || '',
            jobType: question.JobType || 'technical',
        });
        setAudioFile(null);
        setAudioFemaleFile(null);
        setShowModal(true);
    };

    const handleSubmit = async () => {
        const questionText = form.questionText.trim();
        if (!questionText) {
            alert('Question text is required');
            return;
        }
        if (!CATEGORIES.includes(form.category)) {
            alert('Category is required');
            return;
        }
        if (!JOB_TYPES.includes(form.jobType)) {
            alert('Job type is required');
            return;
        }
        setSaving(true);
        try {
            let payload;
            let config;
            if (audioFile || audioFemaleFile) {
                payload = new FormData();
                payload.append('question_text', questionText);
                payload.append('category', form.category);
                if (form.industryId) payload.append('industry_id', form.industryId);
                if (form.difficultyLevel) payload.append('difficulty_level', form.difficultyLevel);
                payload.append('job_type', form.jobType);
                if (audioFile) payload.append('question_audio', audioFile, audioFile.name);
                if (audioFemaleFile) payload.append('question_audio_female', audioFemaleFile, audioFemaleFile.name);
                config = { headers: { 'Content-Type': undefined } };
            } else {
                payload = {
                    question_text: questionText,
                    category: form.category,
                    industry_id: form.industryId || null,
                    difficulty_level: form.difficultyLevel || null,
                    job_type: form.jobType,
                };
            }
            if (editingQuestion) {
                await api.put(`/interview/questions/${editingQuestion.QuestionID}`, payload, config);
            } else {
                await api.post('/interview/questions', payload, config);
            }
            setShowModal(false);
            setAudioFile(null);
            setAudioFemaleFile(null);
            await fetchQuestions();
        } catch (err) {
            console.error('Error saving question:', err);
            alert(err.response?.data?.error || 'Failed to save question');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (question) => {
        setSavingId(question.QuestionID);
        try {
            if (question.isActive) {
                await api.delete(`/interview/questions/${question.QuestionID}`);
            } else {
                await api.post(`/interview/questions/${question.QuestionID}/reactivate`);
            }
            await fetchQuestions();
        } catch (err) {
            console.error('Error updating question status:', err);
            alert(err.response?.data?.error || 'Failed to update question status');
        } finally {
            setSavingId(null);
        }
    };

    const handleDownloadTemplate = async () => {
        setBatchDownloading(true);
        try {
            const response = await api.get('/interview/questions/template', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'MockInterview_Question_Import_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading template:', err);
            setBatchError('Failed to download template');
        } finally {
            setBatchDownloading(false);
        }
    };

    const handleBatchUpload = async () => {
        if (!batchFile) return;
        setBatchUploading(true);
        setBatchError('');
        setBatchResult(null);
        try {
            const formData = new FormData();
            formData.append('file', batchFile);
            const response = await api.post('/interview/questions/batch', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 5 * 60 * 1000,
            });
            setBatchResult(response.data);
            setBatchFile(null);
        } catch (err) {
            console.error('Error batch-importing questions:', err);
            setBatchError(err.response?.data?.error || 'Failed to import questions');
        } finally {
            setBatchUploading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.per_page));

    const cardClass = `rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const inputClass = `capitalize px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow ${
        isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'
    }`;

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-8">
                    <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Interview Management</h1>
                    <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage the Mock Interview question bank</p>
                </header>

                {/* Tabs */}
                <div className={`mb-6 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex gap-4">
                        <button
                            onClick={() => setActiveTab('questions')}
                            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                activeTab === 'questions'
                                    ? 'border-blue-600 text-blue-600'
                                    : isDarkMode
                                        ? 'border-transparent text-slate-400 hover:text-slate-200'
                                        : 'border-transparent text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <CircleQuestionMark size={18} className="inline mr-2" />
                            Questions
                        </button>
                        <button
                            onClick={() => setActiveTab('batch')}
                            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                activeTab === 'batch'
                                    ? 'border-blue-600 text-blue-600'
                                    : isDarkMode
                                        ? 'border-transparent text-slate-400 hover:text-slate-200'
                                        : 'border-transparent text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <FileSpreadsheet size={18} className="inline mr-2" />
                            Batch Upload
                        </button>
                        <button
                            onClick={() => setActiveTab('scoring')}
                            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                activeTab === 'scoring'
                                    ? 'border-blue-600 text-blue-600'
                                    : isDarkMode
                                        ? 'border-transparent text-slate-400 hover:text-slate-200'
                                        : 'border-transparent text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <Scale size={18} className="inline mr-2" />
                            Scoring
                        </button>
                        <button
                            onClick={() => setActiveTab('prompt')}
                            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
                                activeTab === 'prompt'
                                    ? 'border-blue-600 text-blue-600'
                                    : isDarkMode
                                        ? 'border-transparent text-slate-400 hover:text-slate-200'
                                        : 'border-transparent text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <FileText size={18} className="inline mr-2" />
                            AI Prompt
                        </button>
                    </div>
                </div>

                {error && (
                    <div className={`mb-4 p-4 border rounded-lg ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'}`}>
                        {error}
                    </div>
                )}

                {activeTab === 'questions' && (
                    <div className={cardClass}>
                        {/* Filter bar */}
                        <div className={`p-5 border-b flex flex-wrap items-center gap-3 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className="relative flex-1 min-w-[200px]">
                                <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                                    placeholder="Search question text..."
                                    className={`${inputClass} w-full pl-9`}
                                />
                            </div>
                            <select
                                value={categoryFilter}
                                onChange={(e) => { setCategoryFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                                className={inputClass}
                            >
                                <option value="">All Categories</option>
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select
                                value={industryFilter}
                                onChange={(e) => { setIndustryFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                                className={inputClass}
                            >
                                <option value="">All Industries</option>
                                {industries.map((ind) => (
                                    <option key={ind.IndustryID} value={ind.IndustryID}>{ind.IndustryName}</option>
                                ))}
                            </select>
                            <select
                                value={jobTypeFilter}
                                onChange={(e) => { setJobTypeFilter(e.target.value); setPagination((p) => ({ ...p, page: 1 })); }}
                                className={inputClass}
                            >
                                <option value="">All Job Types</option>
                                {JOB_TYPES.map((jt) => <option key={jt} value={jt} className="capitalize">{jt}</option>)}
                            </select>
                            <div className={`flex rounded-lg overflow-hidden border ${isDarkMode ? 'border-slate-600' : 'border-slate-300'}`}>
                                {[
                                    { key: 'active', label: 'Active' },
                                    { key: 'inactive', label: 'Inactive' },
                                    { key: 'all', label: 'All' },
                                ].map((st) => (
                                    <button
                                        key={st.key}
                                        onClick={() => { setStatusFilter(st.key); setPagination((p) => ({ ...p, page: 1 })); }}
                                        className={`px-3 py-2 text-xs font-semibold transition-colors ${
                                            statusFilter === st.key
                                                ? 'bg-primary-600 text-white'
                                                : isDarkMode
                                                    ? 'bg-slate-900 text-slate-300 hover:bg-slate-700'
                                                    : 'bg-white text-slate-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        {st.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={openAddModal}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:scale-[0.97] transition-all flex items-center gap-2 text-sm font-semibold shadow-sm"
                            >
                                <Plus size={16} />
                                Add Question
                            </button>
                        </div>

                        {/* Table */}
                        {loading ? (
                            <InlineTableSkeleton isDarkMode={isDarkMode} rows={8} cols={6} bordered={false} />
                        ) : questions.length === 0 ? (
                            <p className={`text-center py-16 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                No questions found. Add one to get started.
                            </p>
                        ) : (
                            <>
                                <div className={`hidden md:grid grid-cols-[1fr_4fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-4 px-5 py-3 text-xs font-bold uppercase tracking-wide ${
                                    isDarkMode ? 'text-slate-400 border-b border-slate-700' : 'text-slate-500 border-b border-slate-200'
                                }`}>
                                    <div>Category</div>
                                    <div>Question</div>
                                    <div>Industry</div>
                                    <div>Difficulty</div>
                                    <div>Job Type</div>
                                    <div>Status</div>
                                    <div>Audio Path</div>
                                    <div>Actions</div>
                                </div>
                                {questions.map((q) => (
                                    <div
                                        key={q.QuestionID}
                                        className={`grid grid-cols-2 md:grid-cols-[1fr_4fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-3 items-center px-5 py-4 ${
                                            isDarkMode ? 'border-b border-slate-700/60 hover:bg-slate-700/30' : 'border-b border-slate-100 hover:bg-slate-50'
                                        }`}
                                    >
                                        <div className={`text-xs font-bold uppercase tracking-wide ${CAT_COLORS[q.Category]}`}>
                                            {q.Category}
                                        </div>
                                        <div className={`text-sm truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`} title={q.QuestionText}>
                                            {q.QuestionText}
                                        </div>
                                        <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {q.IndustryName || <span className="opacity-50">Any</span>}
                                        </div>
                                        <div className={`text-sm capitalize ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {q.DifficultyLevel || <span className="opacity-50">Any</span>}
                                        </div>
                                        <div className={`inline-flex items-center w-fit text-xs font-semibold capitalize px-2 py-1 rounded-full ${
                                            q.JobType === 'technical'
                                                ? (isDarkMode ? 'bg-blue-400/10 text-blue-400' : 'bg-blue-500/10 text-blue-600')
                                                : q.JobType === 'non-technical' 
                                                        ? (isDarkMode ? 'bg-purple-400/10 text-purple-400' : 'bg-purple-500/10 text-purple-600')
                                                        : (isDarkMode ? 'bg-amber-400/10 text-amber-400': 'bg-amber-500/10 text-amber-600')
                                        }`}>
                                            {q.JobType}
                                        </div>
                                        <div className={`inline-flex items-center gap-1.5 text-xs font-semibold w-fit px-2 py-1 rounded-full border ${
                                            q.isActive
                                                ? (isDarkMode ? 'border-primary-400/40 bg-primary-400/10 text-primary-400' : 'border-primary-500/50 bg-primary-500/10 text-primary-600')
                                                : (isDarkMode ? 'border-slate-600 bg-slate-700/50 text-slate-400' : 'border-slate-300 bg-slate-100 text-slate-500')
                                        }`}>
                                            {q.isActive ? <CheckCircle size={13} /> : <XCircle size={13} />}
                                            {q.isActive ? 'Active' : 'Inactive'}
                                        </div>
                                        {/* Audio Path */}
                                        {(() => {
                                            const status = audioCoverage(q);
                                            const styles = {
                                                complete: isDarkMode ? 'border-primary-400/40 bg-primary-400/10 text-primary-400' : 'border-primary-500/50 bg-primary-500/10 text-primary-600',
                                                partial: isDarkMode ? 'border-yellow-400/40 bg-yellow-400/10 text-yellow-400' : 'border-yellow-500/50 bg-yellow-500/10 text-yellow-600',
                                                none: isDarkMode ? 'border-slate-600 bg-slate-700/50 text-slate-400' : 'border-slate-300 bg-slate-100 text-slate-500',
                                            };
                                            const labels = { complete: 'Complete', partial: 'Partial', none: 'None' };
                                            const Icon = status === 'complete' ? CheckCircle : status === 'partial' ? AlertTriangle : XCircle;
                                            return (
                                                <div className={`inline-flex items-center gap-1.5 text-xs font-semibold w-fit px-2 py-1 rounded-full border ${styles[status]}`}>
                                                    <Icon size={13} />
                                                    {labels[status]}
                                                </div>
                                            );
                                        })()}
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => openEditModal(q)}
                                                title="View / Edit"
                                                className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-blue-400/10' : 'text-blue-600 hover:bg-blue-50'}`}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleToggleActive(q)}
                                                disabled={savingId === q.QuestionID}
                                                title={q.isActive ? 'Deactivate' : 'Reactivate'}
                                                className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                                                    q.isActive
                                                        ? (isDarkMode ? 'text-red-400 hover:bg-red-400/10' : 'text-red-500 hover:bg-red-50')
                                                        : (isDarkMode ? 'text-primary-400 hover:bg-primary-400/10' : 'text-primary-600 hover:bg-primary-50')
                                                }`}
                                            >
                                                {savingId === q.QuestionID
                                                    ? <Loader2 size={16} className="animate-spin" />
                                                    : (q.isActive ? <XCircle size={16} /> : <RotateCcw size={16} />)}
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className={`flex items-center justify-between px-5 py-4 ${isDarkMode ? 'border-t border-slate-700' : 'border-t border-slate-200'}`}>
                                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Showing {questions.length} of {pagination.total} questions
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                                                disabled={pagination.page === 1}
                                                className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                    isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'
                                                }`}
                                            >
                                                <ChevronLeft size={18} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
                                            </button>
                                            <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Page {pagination.page} of {totalPages}</span>
                                            <button
                                                onClick={() => setPagination((p) => ({ ...p, page: Math.min(totalPages, p.page + 1) }))}
                                                disabled={pagination.page === totalPages}
                                                className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                                                    isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'
                                                }`}
                                            >
                                                <ChevronRight size={18} className={isDarkMode ? 'text-slate-300' : 'text-slate-700'} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'batch' && (
                    <div className={`${cardClass} p-6`}>
                        <div className="flex items-center gap-3 mb-1">
                            <FileSpreadsheet className="text-primary-500" size={22} />
                            <h2 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                Batch Add Questions
                            </h2>
                        </div>
                        <p className={`mb-5 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                            Download the template, fill in one question per row, then upload it here to add many
                            questions at once. Question Text, Category, and Job Type are required per row —
                            Difficulty and Industry are optional.
                        </p>

                        {batchError && (
                            <div className={`mb-5 p-4 border rounded-lg flex items-start gap-3 ${isDarkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-800'}`}>
                                <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                                <p className="text-sm flex-1">{batchError}</p>
                                <button onClick={() => setBatchError('')} className={`p-1 rounded ${isDarkMode ? 'hover:bg-red-800/50' : 'hover:bg-red-100'}`}>
                                    <X size={14} />
                                </button>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 mb-6">
                            <button
                                onClick={handleDownloadTemplate}
                                disabled={batchDownloading}
                                className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                                    isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                                }`}
                            >
                                {batchDownloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                                Download Template
                            </button>

                            <label className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                                batchFile
                                    ? (isDarkMode ? 'border border-slate-600 text-slate-300 hover:bg-slate-700' : 'border border-slate-300 text-slate-700 hover:bg-slate-50')
                                    : (isDarkMode ? 'border border-dashed border-slate-600 text-slate-400 hover:bg-slate-700' : 'border border-dashed border-slate-300 text-slate-500 hover:bg-slate-50')
                            }`}>
                                <FileSpreadsheet size={16} />
                                {batchFile ? batchFile.name : 'Choose .xlsx File'}
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    className="hidden"
                                    disabled={batchUploading}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        setBatchResult(null);
                                        setBatchError('');
                                        if (file) setBatchFile(file);
                                        e.target.value = '';
                                    }}
                                />
                            </label>

                            <button
                                onClick={handleBatchUpload}
                                disabled={!batchFile || batchUploading}
                                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 active:scale-[0.97] transition-all text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {batchUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                                {batchUploading ? 'Uploading...' : 'Upload & Import'}
                            </button>
                        </div>

                        {batchResult && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-4">
                                    <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-primary-900/20 border-primary-800' : 'bg-primary-50 border-primary-200'}`}>
                                        <div className={`text-2xl font-bold ${isDarkMode ? 'text-primary-400' : 'text-primary-700'}`}>{batchResult.saved ?? 0}</div>
                                        <div className={`text-sm ${isDarkMode ? 'text-primary-500' : 'text-primary-600'}`}>Saved</div>
                                    </div>
                                    <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200'}`}>
                                        <div className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{batchResult.warnings ?? 0}</div>
                                        <div className={`text-sm ${isDarkMode ? 'text-yellow-500' : 'text-yellow-600'}`}>Saved with Warning</div>
                                    </div>
                                    <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                                        <div className={`text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{batchResult.errors ?? 0}</div>
                                        <div className={`text-sm ${isDarkMode ? 'text-red-500' : 'text-red-600'}`}>Errors</div>
                                    </div>
                                </div>

                                {batchResult.row_results?.length > 0 && (
                                    <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                        <div className={`px-4 py-3 border-b text-sm font-medium ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                            Row-by-Row Results ({batchResult.total_rows} row{batchResult.total_rows !== 1 ? 's' : ''} processed)
                                        </div>
                                        <div className="max-h-72 overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
                                                    {batchResult.row_results.map((r, idx) => (
                                                        <tr key={idx} className={isDarkMode ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}>
                                                            <td className={`px-4 py-2 w-20 font-mono text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                                Row {r.row}
                                                            </td>
                                                            <td className="px-4 py-2 w-40">
                                                                {r.status === 'saved' && (
                                                                    <span className="flex items-center gap-1 text-primary-600 font-medium text-xs">
                                                                        <CheckCircle size={13} /> Saved
                                                                    </span>
                                                                )}
                                                                {r.status === 'saved_with_warning' && (
                                                                    <span className={`flex items-center gap-1 font-medium text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                                                        <AlertTriangle size={13} /> Saved w/ Warning
                                                                    </span>
                                                                )}
                                                                {r.status === 'error' && (
                                                                    <span className="flex items-center gap-1 text-red-600 font-medium text-xs">
                                                                        <XCircle size={13} /> Error
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className={`px-4 py-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                                {r.detail}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <button
                                    onClick={() => setBatchResult(null)}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                                        isDarkMode ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                                    }`}
                                >
                                    <X size={16} /> Clear Results
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'scoring' && (
                    <div>
                        <div className="flex gap-2 mb-4">
                            {[{ key: 'general', label: 'General' }, { key: 'star', label: 'STAR' }].map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => setScoringMode(m.key)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                        scoringMode === m.key
                                            ? 'bg-primary-600 text-white'
                                            : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        <ScoringTab
                            key={scoringMode}
                            isDarkMode={isDarkMode}
                            scoringBase="/admin/interview-scoring/categories"
                            scoringType={scoringMode}
                            blurb={scoringMode === 'star'
                                ? 'Configure the STAR (Situation / Task / Action / Result) components, weights, and rubrics used to grade behavioral responses'
                                : 'Configure the categories, weights, and grading rubrics used for AI-powered mock interview scoring'}
                        />
                    </div>
                )}
                {activeTab === 'prompt' && (
                    <div>
                        <div className="flex gap-2 mb-4">
                            {[{ key: 'general', label: 'General' }, { key: 'star', label: 'STAR' }].map((m) => (
                                <button
                                    key={m.key}
                                    onClick={() => setPromptMode(m.key)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                        promptMode === m.key
                                            ? 'bg-primary-600 text-white'
                                            : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                        <PromptTab key={promptMode} isDarkMode={isDarkMode} featureKey={promptMode === 'star' ? 'star_analysis' : 'mock_interview'} />
                    </div>
                )}

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
                        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowModal(false)} />
                        <div className={`relative w-full max-w-lg max-h-[85vh] flex flex-col rounded-3xl shadow-2xl overflow-hidden ${
                            isDarkMode ? 'bg-slate-900 border border-slate-700' : 'bg-white'
                        }`}>
                            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    {editingQuestion ? 'Edit Question' : 'Add Question'}
                                </h3>
                                <button
                                    onClick={() => setShowModal(false)}
                                    type="button"
                                    className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}
                                >
                                    <X className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                                </button>
                            </div>

                            <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                <div>
                                    <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                        Question Text
                                    </label>
                                    <textarea
                                        value={form.questionText}
                                        onChange={(e) => setForm({ ...form, questionText: e.target.value })}
                                        rows={4}
                                        placeholder="e.g. Tell me about a time you disagreed with a teammate..."
                                        className={`${inputClass} w-full`}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Category <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={form.category}
                                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                                            className={`${inputClass} w-full`}
                                        >
                                            <option value="" disabled>-- Select Category --</option>
                                            {CATEGORIES.map((c) => <option key={c} value={c} className='capitalize'>{c}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Job Type <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={form.jobType}
                                            onChange={(e) => setForm({ ...form, jobType: e.target.value })}
                                            className={`${inputClass} w-full capitalize`}
                                        >
                                            <option value="" disabled>-- Select Job Type --</option>
                                            {JOB_TYPES.map((jt) => <option key={jt} value={jt} className="capitalize">{jt}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Difficulty
                                        </label>
                                        <select
                                            value={form.difficultyLevel}
                                            onChange={(e) => setForm({ ...form, difficultyLevel: e.target.value })}
                                            className={`${inputClass} w-full`}
                                        >
                                            <option value="">Any</option>
                                            {DIFFICULTIES.map((d) => <option key={d} value={d} className="capitalize">{d}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                            Industry
                                        </label>
                                        <select
                                            value={form.industryId}
                                            onChange={(e) => setForm({ ...form, industryId: e.target.value })}
                                            className={`${inputClass} w-full`}
                                        >
                                            <option value="">Any Industry</option>
                                            {industries.map((ind) => (
                                                <option key={ind.IndustryID} value={ind.IndustryID}>{ind.IndustryName}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <AudioPickerField
                                    label="Question Audio (Male / Default)"
                                    file={audioFile}
                                    setFile={setAudioFile}
                                    existingBlobPath={editingQuestion?.BlobPath}
                                    isDarkMode={isDarkMode}
                                />
                                <AudioPickerField
                                    label="Question Audio (Female)"
                                    file={audioFemaleFile}
                                    setFile={setAudioFemaleFile}
                                    existingBlobPath={editingQuestion?.BlobPathFemale}
                                    isDarkMode={isDarkMode}
                                />
                            </div>

                            <div className={`flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t shrink-0 ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                                        isDarkMode ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-500 disabled:opacity-50 transition-colors flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

